import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function SOPUploadDialog({ open, onOpenChange, employees, onSaved }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [visibleTo, setVisibleTo] = useState([]);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(""); setCategory(""); setDescription(""); setVisibleTo([]); setFile(null);
    }
  }, [open]);

  const toggleEmployee = (id) => setVisibleTo((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const handleSubmit = async () => {
    if (!title.trim() || !category.trim() || !file) {
      toast.error("Title, category, and a file are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("category", category.trim());
      fd.append("description", description.trim());
      fd.append("visible_to", visibleTo.join(","));
      fd.append("file", file);
      await api.post("/sops", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SOP published");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="sop-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Publish New SOP</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="sop-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Client Data Handling SOP" />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Input data-testid="sop-category-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Data Governance" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea data-testid="sop-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>Visible To (leave empty for all employees)</Label>
            <div className="border border-[#E2E8F0] rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
              {employees.map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-sm px-1 py-0.5 cursor-pointer">
                  <Checkbox data-testid={`sop-visible-checkbox-${e.id}`} checked={visibleTo.includes(e.id)} onCheckedChange={() => toggleEmployee(e.id)} />
                  {e.name}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>SOP File</Label>
            <Input data-testid="sop-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sop-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="sop-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Publishing..." : "Publish SOP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
